import { useState, type FormEvent } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/dag3p63ep053ijub2330';

type Status = { kind: 'idle' | 'loading' | 'success' | 'error'; msg?: string };

export default function Newsletter() {
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const email = String(fd.get('email') ?? '').trim();
    const honeypot = String(fd.get('phone_alt') ?? '').trim();

    // 反垃圾：蜜罐命中时给出与成功一致的回显，但不真正提交
    if (honeypot) {
      setStatus({ kind: 'success', msg: '订阅成功，已加入追更队列，我们每周一不见不散。' });
      return;
    }

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus({ kind: 'error', msg: '请填写正确的邮箱地址后再提交。' });
      return;
    }

    setStatus({ kind: 'loading' });

    const payload = new URLSearchParams();
    payload.set('email', email);

    try {
      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const codeOk = parsed?.code === 'OK';
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || (parsed && 'code' in parsed ? '' : '') || '';

      if (res.ok && codeOk) {
        form.reset();
        setStatus({ kind: 'success', msg: '订阅成功，已加入追更队列，我们每周一不见不散。' });
      } else {
        const showMsg = serverMsg || (res.ok ? '提交失败，请稍后再试。' : '网络异常，请稍后再试。');
        setStatus({ kind: 'error', msg: showMsg });
      }
    } catch {
      setStatus({ kind: 'error', msg: '网络异常，请稍后再试。' });
    }
  }

  return (
    <section id="subscribe" className="relative scroll-mt-16 bg-background-100 py-16 md:py-20">
      <div className="mx-auto max-w-[1240px] px-4 md:px-6">
        <div className="relative overflow-hidden rounded-2xl bg-primary-500 px-6 py-12 md:px-12 md:py-16">
          <div className="pointer-events-none absolute -right-16 -top-20 h-72 w-72 rounded-full bg-accent-500/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 left-1/4 h-64 w-64 rounded-full bg-primary-950/20 blur-3xl" />

          <div className="relative flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-background-950/20 px-3 py-1 text-xs font-bold text-background-50 backdrop-blur">
                <i className="ri-mail-volume-fill" aria-hidden="true"></i>
                每周一 · 精华盘点准时推送
              </span>
              <h2 className="mt-4 text-2xl font-black leading-tight text-background-50 md:text-4xl">
                别再全网刷瓜了，
                <br className="hidden sm:block" />
                一键订阅，精彩不漏
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-background-100/85 md:text-base">
                留下邮箱即可订阅「瓜友观察站」周更精华：本周热榜、名场面合集与年度盘点抢先送达。
                绝不外泄邮箱，可随时退订。
              </p>
            </div>

            <div className="w-full max-w-md">
              {status.kind === 'success' ? (
                <div className="rounded-xl bg-background-50/95 p-6 text-center">
                  <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-accent-500 text-xl text-background-950">
                    <i className="ri-check-line" aria-hidden="true"></i>
                  </span>
                  <p className="mt-3 text-sm font-semibold text-foreground-950">{status.msg}</p>
                  <button
                    type="button"
                    onClick={() => setStatus({ kind: 'idle' })}
                    className="mt-3 text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    再订阅一个邮箱
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form="entertainment-subscribe"
                  onSubmit={handleSubmit}
                  className="rounded-xl bg-background-50/95 p-5"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="relative flex-1">
                      <i
                        className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-400"
                        aria-hidden="true"
                      ></i>
                      <input
                        type="email"
                        name="email"
                        required
                        autoComplete="email"
                        placeholder="输入你的邮箱"
                        aria-label="邮箱地址"
                        className="w-full rounded-md border border-background-200 bg-background-50 py-3 pl-10 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={status.kind === 'loading'}
                      className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-bold text-background-950 transition-colors hover:bg-accent-400 disabled:opacity-70"
                    >
                      {status.kind === 'loading' ? (
                        <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                      ) : (
                        <>
                          立即订阅
                          <i className="ri-send-plane-fill ml-1.5" aria-hidden="true"></i>
                        </>
                      )}
                    </button>
                  </div>
                  {/* 反垃圾蜜罐 */}
                  <div className="hp-guard" aria-hidden="true">
                    <label htmlFor="phone-alt">备用联系方式</label>
                    <input
                      id="phone-alt"
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      readOnly
                    />
                  </div>
                  {status.kind === 'error' && (
                    <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-primary-800">
                      <i className="ri-error-warning-fill" aria-hidden="true"></i>
                      {status.msg}
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}